/**
 * "How many do I need?" (sections/hf-planner.liquid). Need = 2 bath + 2 wash per person, 2 hand per bathroom,
 * 1 of each per guest. Tries every count of the set of 6 and fills the rest with single-type sets; keeps the
 * cheapest mix (fewest pieces on a tie). Prices and URLs come from the section's JSON (live product data, cents).
 */
const CONTENTS = {
  starter: { b: 2, h: 2, w: 2 },
  bath: { b: 4, h: 0, w: 0 },
  hand: { b: 0, h: 6, w: 0 },
  wash: { b: 0, h: 0, w: 12 },
};

const formatMoney = (cents) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100).replace('CA', '');

function cheapestMix(sets, { people, baths, guests }) {
  const need = { b: 2 * people + guests, h: 2 * baths + guests, w: 2 * people + guests };
  const maxStarter = sets.starter ? Math.ceil(Math.max(need.b, need.h, need.w) / 2) : 0;
  let best = null;
  for (let s = 0; s <= maxStarter; s++) {
    const counts = {
      starter: s,
      bath: Math.ceil(Math.max(0, need.b - 2 * s) / 4),
      hand: Math.ceil(Math.max(0, need.h - 2 * s) / 6),
      wash: Math.ceil(Math.max(0, need.w - 2 * s) / 12),
    };
    if (Object.keys(counts).some((k) => counts[k] > 0 && !sets[k])) continue;
    const cost = Object.keys(counts).reduce((sum, k) => sum + counts[k] * (sets[k]?.price ?? 0), 0);
    const pieces = Object.keys(counts).reduce((sum, k) => sum + counts[k] * (sets[k]?.pieces ?? 0), 0);
    if (!best || cost < best.cost || (cost === best.cost && pieces < best.pieces)) best = { counts, cost, pieces };
  }
  return best;
}

class HfPlanner extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-planner-data]')?.textContent;
    if (!json) return;
    this.data = JSON.parse(json);
    this.state = { people: 2, baths: 1, guests: 0, line: this.dataset.line || 'zero-twist' };

    this.querySelectorAll('.hf-planner__stepper').forEach((stepper) => {
      const key = stepper.dataset.key;
      const min = Number(stepper.dataset.min);
      const max = Number(stepper.dataset.max);
      const output = stepper.querySelector('output');
      const [minus, plus] = stepper.querySelectorAll('button');
      const sync = () => {
        output.textContent = String(this.state[key]);
        minus.disabled = this.state[key] <= min;
        plus.disabled = this.state[key] >= max;
      };
      stepper.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', () => {
          const next = Math.min(max, Math.max(min, this.state[key] + Number(button.dataset.step)));
          if (next === this.state[key]) return;
          this.state[key] = next;
          sync();
          this.render();
        })
      );
      sync();
    });

    this.querySelectorAll('input[type="radio"]').forEach((radio) =>
      radio.addEventListener('change', () => {
        this.state.line = radio.value;
        this.render();
      })
    );
    this.render();
  }

  render() {
    const sets = this.data.lines[this.state.line];
    if (!sets) return;
    const mix = cheapestMix(sets, this.state);
    if (!mix) return;

    const items = Object.keys(mix.counts)
      .filter((k) => mix.counts[k] > 0)
      .map((k) => {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.href = sets[k].url;
        link.textContent = `${mix.counts[k]} × ${sets[k].title}`;
        const price = document.createElement('span');
        price.textContent = formatMoney(mix.counts[k] * sets[k].price);
        li.append(link, price);
        return li;
      });
    this.querySelector('[data-sets]').replaceChildren(...items);
    this.querySelector('[data-total]').textContent = formatMoney(mix.cost);
    this.querySelector('[data-per]').textContent = `${mix.pieces} pieces · ${formatMoney(Math.round(mix.cost / mix.pieces))} a piece`;

    const free = this.data.free;
    const bar = this.querySelector('[data-bar]');
    const ship = this.querySelector('[data-ship]');
    bar.style.width = `${Math.min(100, (mix.cost / free) * 100)}%`;
    ship.textContent =
      mix.cost >= free
        ? 'Ships free across Canada.'
        : `${formatMoney(free - mix.cost)} more and it ships free across Canada.`;
  }
}

if (!customElements.get('hf-planner')) customElements.define('hf-planner', HfPlanner);
